import { useState, type FormEvent } from "react";
import { Link, createRoute, useParams } from "@tanstack/react-router";
import { ApiError } from "@plotlounge/shared/api";
import { publicRsvpSchema } from "@plotlounge/shared/validations";
import {
  usePublicGatheringDetail,
  type PublicGatheringDetail,
} from "@/lib/discover/queries";
import { CaptchaGate } from "@/components/captcha/CaptchaGate";
import { getApiClient } from "@/lib/api-client";
import { NotFound } from "@/components/NotFound";
import { useRobotsMeta } from "@/lib/seo/useRobotsMeta";
import { rootRoute } from "./root";
import styles from "./gathering-detail.module.scss";

function GatheringDetailRoute() {
  const params = useParams({ from: gatheringDetailRoute.id });
  const { data, isLoading, isError, error } = usePublicGatheringDetail(
    params.id,
  );

  if (isLoading) {
    return (
      <main className={styles.loading} role="status" aria-live="polite">
        Loading…
      </main>
    );
  }
  if (isError) {
    if (error instanceof ApiError && error.status === 404) {
      return <NotFound />;
    }
    return (
      <main className={styles.loading} role="alert">
        Couldn't load this gathering.
      </main>
    );
  }
  if (!data) return <NotFound />;

  return <Detail gathering={data} />;
}

function Detail({ gathering }: { gathering: PublicGatheringDetail }) {
  const remaining =
    gathering.capacity != null
      ? Math.max(0, gathering.capacity - gathering.rsvpCount)
      : null;

  // Past gatherings keep rendering so existing indexed URLs do not 404, but
  // we tell crawlers not to re-index them (issue #296).
  useRobotsMeta(gathering.isPast ? "noindex" : null);

  return (
    <main className={styles.root}>
      <header className={styles.topbar}>
        <Link to="/" className={styles.brand}>
          Plotlounge
        </Link>
        <Link to="/discover" className={styles.back}>
          ← All gatherings
        </Link>
      </header>

      <article className={styles.article}>
        <header className={styles.hero}>
          <p className={styles.eyebrow}>
            {gathering.club.name}
            {gathering.city ? ` · ${gathering.city}` : ""}
          </p>
          <h1 className={styles.headline}>{gathering.title}</h1>
          {gathering.isPast ? (
            <p className={styles.pastBadge} role="status">
              This gathering has already happened.
            </p>
          ) : null}
          {gathering.description ? (
            <p className={styles.deck}>{gathering.description}</p>
          ) : null}
          <dl className={styles.metaGrid}>
            <div>
              <dt>When</dt>
              <dd>
                {formatLongDate(gathering.date)}
                <br />
                {formatTime(gathering.date)}
                {gathering.endDate ? ` – ${formatTime(gathering.endDate)}` : ""}
              </dd>
            </div>
            <div>
              <dt>Where</dt>
              <dd>
                {gathering.venueName ?? gathering.neighborhood ?? "TBA"}
                {gathering.address ? (
                  <>
                    <br />
                    {gathering.address}
                  </>
                ) : null}
                {!gathering.address && gathering.neighborhood ? (
                  <>
                    <br />
                    {gathering.neighborhood}
                  </>
                ) : null}
              </dd>
            </div>
            <div>
              <dt>Who</dt>
              <dd>
                {gathering.rsvpCount} attending
                {remaining != null && gathering.capacity != null ? (
                  <>
                    <br />
                    {remaining} of {gathering.capacity} seats left
                  </>
                ) : null}
              </dd>
            </div>
          </dl>
        </header>

        <section className={styles.body}>
          <h2 className={styles.h2}>About this gathering</h2>
          <p>
            We meet table-style. Bring your copy of the book and a question or
            two. Cancellations are easier than no-shows — your confirmation
            email includes a one-click cancel link.
          </p>
          {gathering.club.description ? (
            <>
              <h2 className={styles.h2}>About {gathering.club.name}</h2>
              <p>{gathering.club.description}</p>
            </>
          ) : null}
        </section>

        {gathering.isPast ? null : (
          <RsvpBlock
            gatheringId={gathering.id}
            capacity={gathering.capacity}
            rsvpCount={gathering.rsvpCount}
          />
        )}
      </article>
    </main>
  );
}

// ─── RSVP block ──────────────────────────────────────────────────────────────

interface RsvpBlockProps {
  gatheringId: string;
  capacity: number | null;
  rsvpCount: number;
}

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success"; alreadyExisted: boolean }
  | { kind: "error"; message: string };

function RsvpBlock({ gatheringId, capacity, rsvpCount }: RsvpBlockProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status.kind === "submitting") return;
    if (!captchaToken) {
      setStatus({
        kind: "error",
        message: "Please confirm you're not a robot.",
      });
      return;
    }

    const parsed = publicRsvpSchema.safeParse({ name, email });
    if (!parsed.success) {
      setStatus({
        kind: "error",
        message: parsed.error.issues[0]?.message ?? "Check your details.",
      });
      return;
    }

    setStatus({ kind: "submitting" });
    try {
      const res = await getApiClient().post<{
        id: string;
        cancelToken: string;
        alreadyExisted: boolean;
      }>(`/public/gatherings/${gatheringId}/rsvp`, {
        ...parsed.data,
        captchaToken,
      });
      setStatus({ kind: "success", alreadyExisted: res.alreadyExisted });
    } catch (err) {
      const message =
        err instanceof ApiError ? errorMessageFor(err) : "Try again later.";
      setStatus({ kind: "error", message });
    }
  }

  if (status.kind === "success") {
    return (
      <section className={styles.rsvpBlock} aria-live="polite">
        <header className={styles.rsvpHead}>
          <h2 className={styles.rsvpTitle}>You're in</h2>
        </header>
        <p className={styles.rsvpConfirm}>
          {status.alreadyExisted
            ? "We already had your RSVP. Confirmation re-sent."
            : "Confirmation sent — check your email for the address and a cancel link."}
        </p>
      </section>
    );
  }

  const remaining = capacity != null ? Math.max(0, capacity - rsvpCount) : null;

  return (
    <section className={styles.rsvpBlock}>
      <header className={styles.rsvpHead}>
        <h2 className={styles.rsvpTitle}>RSVP</h2>
        {remaining != null && capacity != null ? (
          <p className={styles.rsvpSub}>
            {remaining} of {capacity} seats left
          </p>
        ) : null}
      </header>
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Name</span>
          <input
            type="text"
            placeholder="First and last"
            className={styles.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            required
          />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Email</span>
          <input
            type="email"
            placeholder="you@somewhere.com"
            className={styles.input}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </label>
        <CaptchaGate
          onVerify={(t) => setCaptchaToken(t)}
          onExpire={() => setCaptchaToken(null)}
        />
        <button
          type="submit"
          className={styles.submit}
          disabled={!captchaToken || status.kind === "submitting"}
        >
          {status.kind === "submitting" ? "…" : "Reserve a seat"}
        </button>
        <p className={styles.fineprint}>
          We send a confirmation with the address and a cancel link. No account
          needed. No marketing email.
        </p>
        {status.kind === "error" && (
          <p className={styles.error} role="alert">
            {status.message}
          </p>
        )}
      </form>
    </section>
  );
}

function errorMessageFor(err: ApiError): string {
  if (err.status === 429) {
    return (
      err.message ||
      "Too many RSVPs from this address. Please try again in an hour."
    );
  }
  if (err.status === 404) {
    return "This gathering is no longer accepting RSVPs.";
  }
  if (err.status >= 500) {
    return "Something went wrong on our end. Please try again.";
  }
  return err.message || "Please check your details and try again.";
}

// ─── Date formatting ─────────────────────────────────────────────────────────

function formatLongDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

// ─── Route ───────────────────────────────────────────────────────────────────

export const gatheringDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/g/$id",
  component: GatheringDetailRoute,
});

export { GatheringDetailRoute };
